import React from "react";

import { checkPassword, PASSWORD_RULES } from "@/utils/passwordPolicy";

/**
 * Live password-requirement checklist.
 *
 * Shown under any field where a NEW password is set — signup, checkout signup,
 * reset, and both change-password surfaces. The rules it renders come from
 * `utils/passwordPolicy`, which mirrors the server rule, so the list can never
 * drift from what the API will actually accept.
 *
 * Behaviour decisions worth keeping:
 *  - It appears only once the user starts typing. An untouched field showing a
 *    column of red crosses reads as a telling-off before they have done
 *    anything.
 *  - It disappears again the moment every rule passes, and comes straight back
 *    if the password stops qualifying. A satisfied checklist is just clutter
 *    above the button — its absence IS the confirmation.
 *  - `tone` decides how an unmet rule reads. "neutral" (default) greys it —
 *    "not yet reached". "error" turns it red, for forms where the field itself
 *    also carries a red border while the password does not qualify, so the two
 *    cues agree instead of one shouting and the other shrugging.
 *
 * Responsive: the list is a wrapping flex, so it is a two-column grid where
 * there is room and a single column on a phone, without a media query.
 *
 * `variant` picks the palette. "dark" (default) suits the brown/glass modals;
 * "light" suits the cream account-page cards.
 */

const MET = "#34d399";
const UNMET_ERROR = "#e47a6a";

type Props = {
  password: string;
  /** Hide until the user has engaged with the field. */
  visible?: boolean;
  variant?: "dark" | "light";
  /** How an unmet rule reads — see the note above. */
  tone?: "neutral" | "error";
};

const Tick: React.FC<{ met: boolean; color: string }> = ({ met, color }) => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth={met ? 3 : 2}
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ flexShrink: 0, transition: "stroke 0.2s ease" }}
    aria-hidden
  >
    {met ? <polyline points="20 6 9 17 4 12" /> : <circle cx="12" cy="12" r="4" />}
  </svg>
);

const PasswordRequirements: React.FC<Props> = ({ password, visible = true, variant = "dark", tone = "neutral" }) => {
  if (!visible || !password) return null;

  const check = checkPassword(password);
  // Every rule satisfied → the list has nothing left to say, so it gets out of
  // the way. It returns automatically if an edit breaks a rule again.
  if (Object.values(check).every(Boolean)) return null;

  const unmetColor =
    tone === "error" ? UNMET_ERROR : variant === "light" ? "rgba(58, 38, 22, 0.45)" : "rgba(251, 244, 236, 0.45)";
  const metTextColor = variant === "light" ? "#2f7d5b" : MET;

  return (
    <div
      style={{
        marginTop: 8,
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif',
        fontSize: "0.75rem",
        lineHeight: 1.5,
      }}
    >
      {/* aria-live so a screen reader hears rules being satisfied rather than
          only discovering the problem on submit. */}
      <ul
        aria-live="polite"
        style={{
          listStyle: "none",
          margin: 0,
          padding: 0,
          display: "flex",
          flexWrap: "wrap",
          gap: "3px 14px",
        }}
      >
        {PASSWORD_RULES.map(({ key, label }) => {
          const met = check[key];
          const color = met ? metTextColor : unmetColor;
          return (
            <li
              key={key}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                // Two per row where there is width, one per row on a phone.
                flex: "1 1 clamp(140px, 45%, 220px)",
                color,
                transition: "color 0.2s ease",
              }}
            >
              <Tick met={met} color={color} />
              <span>{label}</span>
            </li>
          );
        })}
      </ul>

    </div>
  );
};

export default PasswordRequirements;
